<script lang="ts">
    import type {DraggableResizerEventData} from 'src/features/sutor/components/draggable-resizer/types';
    import type {CssSize} from 'shared/ui-widgets/types';
    import {exists} from 'shared/utils/custom-utils';
    import {onDestroy, tick} from 'svelte';
    import {get, writable} from 'svelte/store';
    import {persistentStorageLocal} from 'shared/persistent-storage/persistent-storage';
    import ErpPageLayout from 'src/features/erp/components/ErpPageLayout.svelte';
    import SutorDraggableResizer from 'src/features/sutor/components/draggable-resizer/SutorDraggableResizer.svelte';
    import ErpHeadingBar from 'src/features/erp/components/ErpHeadingBar.svelte';

    export let pageClass: string;
    export let ratios: [number, number] = [1, 1];
    export let gap: CssSize = '0px';
    export let withContentPadding = false;

    let windowWidth = window.innerWidth;
    let oldWindowWidth = window.innerWidth;

    let rightPanelElement: HTMLElement;
    const rightPanelWidthRatio$ = writable(ratios[1] / (ratios[0] + ratios[1]));
    const rightPanelWidthPersistentUnsubscribe = persistentStorageLocal(rightPanelWidthRatio$, `${pageClass}-right-panel-width-ratio`);
    let beforeResizingRightPanelWidth: number | null = null;

    $: updateRightPanelWidth(windowWidth);

    onDestroy(() => {
        rightPanelWidthPersistentUnsubscribe();
    });

    const handleResize = (event: CustomEvent<DraggableResizerEventData>) => {
        if (!exists(beforeResizingRightPanelWidth) && event.detail.resizing) {
            beforeResizingRightPanelWidth = get(rightPanelWidthRatio$) * windowWidth;
        }

        if (!event.detail.resizing) {
            beforeResizingRightPanelWidth = null;

            if (exists(rightPanelElement)) {
                tick().then(() => $rightPanelWidthRatio$ = rightPanelElement.getBoundingClientRect().width / windowWidth);
            }
        }

        if (!exists(beforeResizingRightPanelWidth) && !event.detail.resizing) {
            return;
        }

        $rightPanelWidthRatio$ = (beforeResizingRightPanelWidth - event.detail.delta) / windowWidth;
    };

    function updateRightPanelWidth(innerWidth: number) {
        const oldRightPanelWidthRadio = (get(rightPanelWidthRatio$) * innerWidth) / oldWindowWidth;
        $rightPanelWidthRatio$ = (oldRightPanelWidthRadio * innerWidth) / innerWidth;
        oldWindowWidth = innerWidth;
    }
</script>

<svelte:window bind:innerWidth={windowWidth}/>

<ErpPageLayout {pageClass} {gap} withoutContentPadding="{!withContentPadding}">
    {#if exists($$slots.heading)}
        <ErpHeadingBar slot="heading">
            <slot name="heading"/>
        </ErpHeadingBar>
    {/if}

    <div class="page-content-container">
        <div class="left-panel-container">
            <div class="left-panel-inner">
                <slot name="left-panel"/>
            </div>
        </div>

        <SutorDraggableResizer orientation="vertical" on:resize={handleResize}/>

        <div class="right-panel-container" style:--right-panel-width="{$rightPanelWidthRatio$ * windowWidth}px"
             bind:this={rightPanelElement}>

            <div class="right-panel-inner">
                <slot name="right-panel"/>
            </div>
        </div>
    </div>
</ErpPageLayout>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro-erp.less";

    .page-content-container {
        display: flex;
        width: 100%;
        flex: 1 1 0;
        overflow: hidden;

        .left-panel-container {
            overflow-y: scroll;
            flex: 1 1 0;
            display: flex;
            flex-direction: column;

            .left-panel-inner {
                display: flex;
                flex-direction: column;
                flex: 1 1 0;
                padding: @spacing-l @spacing-xl @spacing-xl @spacing-xxl;
            }
        }

        .right-panel-container {
            overflow-y: scroll;
            width: var(--right-panel-width);
            display: flex;
            flex-direction: column;

            .right-panel-inner {
                display: flex;
                flex-direction: column;
                flex: 1 1 0;
                padding: @spacing-l @spacing-xxl @spacing-xl @spacing-xl;
            }
        }
    }
</style>